import merge from "lodash-es/merge"
import { VChip } from "vuetify/components"
import TicketTypeBadge from "../components/TicketTypeBadge.vue"

export enum ListValueType {
  STREET = "STREET",
  CITY = "CITY",
  DISTRICT = "DISTRICT",
  AREA = "AREA",
  MUNICIPALITY = "MUNICIPALITY",
  INSPECTION_DESCRIPTION = "INSPECTION_DESCRIPTION",
  ACTION_DESCRIPTION = "ACTION_DESCRIPTION",
  MALFUNCTION_DESCRIPTION = "MALFUNCTION_DESCRIPTION",
  ARTICLE_CATEGORY = "ARTICLE_CATEGORY",
  INVOICE_PERIOD = "INVOICE_PERIOD",
}

export interface ListValue {
  _id: string
  organization: string
  type: ListValueType
  subtypes?: Array<string>
  description: string
}

export type ListSubtypeOption = string
export type SubtypeOptionsComposable = () => {
  subtypeOptions: Ref<Array<ListSubtypeOption>>
  getSubtypeOptions: () => Promise<Array<ListSubtypeOption>>
  getSubtypeTranslationKey: (subtype: string) => string
  getSubtypeDisplayChip: (subtype: string) => {
    component: Component
    attributes: Record<string, unknown>
    content?: string
  }
}

export type ListComposableReturn = {
  items: Ref<Array<ListValue>>
  getItems(pagination?: boolean, organization?: string): Promise<ListValue[]>
  getItemsById(id: ListValue["_id"]): Promise<ListValue>
  getByPage(queryParameters: Record<string, unknown>): Promise<PaginateResult<ListValue>>
  updateItem(id: ListValue["_id"], item: Partial<ListValue>): Promise<ListValue>
  postItem(item: Partial<ListValue>): Promise<ListValue>
  deleteItem(id: ListValue["_id"]): Promise<ListValue>
} & ReturnType<SubtypeOptionsComposable>

const useInspectionSubtypeOptions: SubtypeOptionsComposable = () => {
  const { inspectionTicketTypes } = useTicketStore()
  const subtypeOptions = ref<Array<ListSubtypeOption>>([])

  const getSubtypeOptions = (): Promise<Array<ListSubtypeOption>> => {
    subtypeOptions.value = [...inspectionTicketTypes]

    return Promise.resolve(subtypeOptions.value)
  }

  const getSubtypeTranslationKey = (inspectionType: string): string => `inspections.type.${inspectionType}`

  const getSubtypeDisplayChip = (inspectionType: string) => ({
    component: TicketTypeBadge,
    attributes: { ticketType: inspectionType },
  })

  return {
    subtypeOptions,
    getSubtypeOptions,
    getSubtypeTranslationKey,
    getSubtypeDisplayChip,
  }
}

const useDefaultSubtypeOptions: SubtypeOptionsComposable = () => {
  const subtypeOptions = ref<Array<ListSubtypeOption>>([])

  const getSubtypeOptions = (): Promise<Array<ListSubtypeOption>> => Promise.resolve(subtypeOptions.value)
  const getSubtypeTranslationKey = (subtype: string) => subtype

  const getSubtypeDisplayChip = (subtype: string) => ({
    component: VChip,
    attributes: {},
    content: subtype,
  })

  return {
    subtypeOptions,
    getSubtypeOptions,
    getSubtypeTranslationKey,
    getSubtypeDisplayChip,
  }
}

const createListStore = (type: ListValueType, useCustomSubtypeOptions?: SubtypeOptionsComposable) =>
  defineStore(`list:${type}`, (): ListComposableReturn => {
    /** @deprecated */
    const items = ref<Array<ListValue>>([])

    const useSubtypeOptions = useCustomSubtypeOptions ?? useDefaultSubtypeOptions
    const { subtypeOptions, getSubtypeOptions, getSubtypeDisplayChip, getSubtypeTranslationKey } = useSubtypeOptions()

    const getItems = async (pagination = false, organization?: string) =>
      useCrudMethods<ListValue>("/list-values", items).readItems({
        query: JSON.stringify({ type, organization }),
        sort: JSON.stringify({ description: 1 }),
        pagination,
      })
    const getItemsById = async (id: ListValue["_id"]) => useCrudMethods<ListValue>("/list-values", items).readItemById(id)
    const updateItem = async (id: ListValue["_id"], item: Partial<ListValue>) =>
      useCrudMethods<ListValue>("/list-values", items).updateItem(id, item)
    const postItem = async (item: Partial<ListValue>) => useCrudMethods<ListValue>("/list-values", items).createItem(item)
    const deleteItem = async (id: ListValue["_id"]) => useCrudMethods<ListValue>("/list-values", items).deleteItem(id)

    const getByPage = async (queryParameters: Record<string, unknown> = {}): Promise<PaginateResult<ListValue>> => {
      const defaultFilter = {
        query: {
          type,
        },
      }

      const combined = merge(defaultFilter, queryParameters)
      const query = getUrlSearchParams(combined)

      const { data } = await useSamApi<PaginateResult<ListValue>>(`/list-values?${query}`)
      return data
    }

    return {
      items,
      getItems,
      getItemsById,
      getByPage,
      updateItem,
      postItem,
      deleteItem,

      subtypeOptions,
      getSubtypeOptions,
      getSubtypeTranslationKey,
      getSubtypeDisplayChip,
    }
  })

export const useStreetStore = createListStore(ListValueType.STREET)
export const useCityStore = createListStore(ListValueType.CITY)
export const useDistrictStore = createListStore(ListValueType.DISTRICT)
export const useAreaStore = createListStore(ListValueType.AREA)
export const useMunicipalityStore = createListStore(ListValueType.MUNICIPALITY)
export const useArticleCategoryStore = createListStore(ListValueType.ARTICLE_CATEGORY)
export const useMalfunctionDescriptionStore = createListStore(ListValueType.MALFUNCTION_DESCRIPTION)
export const useInspectionDescriptionStore = createListStore(ListValueType.INSPECTION_DESCRIPTION, useInspectionSubtypeOptions)
export const useActionDescriptionStore = createListStore(ListValueType.ACTION_DESCRIPTION)
export const useInvoicePeriodStore = createListStore(ListValueType.INVOICE_PERIOD)
